import { parseRetryAfter, readBoundedResponseBytes, speechLocaleTag } from "@rakazo/core";
import { newClientId } from "./client-id.js";
import type { DictationAudioStore, StoredDictationSegment } from "./dictation-audio-store.js";
import { createDictationAudioStore } from "./dictation-audio-store.js";
import { getActiveUiLocale } from "./i18n.js";
import { selectedSpaceId, withSpaceHeaders } from "./rpc.js";
import { errorText, voiceAccountErrorText } from "./user-error.js";

export type DictationMode = "hold" | "endpoint";

export type DictationSnapshot = {
  status: "idle" | "listening" | "transcribing";
  transcript: string;
  error?: string;
};

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

interface SpeechRecognitionLike {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((event: { resultIndex: number; results: SpeechRecognitionResultList }) => void) | null;
  onerror: ((event: { error?: string }) => void) | null;
  onend: (() => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

/** Saved dictation audio that did not reach the composer and can be sent again. */
export type KeptDictation = {
  segments: number;
  retrying: boolean;
  /** Why the provider refused, when it said (translated); otherwise the UI's generic line. */
  error?: string;
};

type Segment = {
  id: string;
  owner: string | null;
  takeId: string;
  index: number;
  createdAt: number;
  spaceId: string | null;
  mimeType: string;
  /** Audio until transcribed. */
  blob?: Blob;
  /** Set once transcribed; the row keeps it until the text reaches the composer. */
  text?: string;
  forgotten?: boolean;
};

type Outcome =
  | { ok: true; text: string }
  | {
      ok: false;
      error: string;
      accountError?: string;
      retryable: boolean;
      retryAfterMs?: number;
    };

/** One recording session on the server path: its pieces, in order, and their progress. */
type Take = {
  id: string;
  mine: number;
  mode: DictationMode;
  spaceId: string | null;
  owner: string | null;
  /** Hold-to-talk keeps audio it could not transcribe; a call turn is not worth replaying. */
  persist: boolean;
  stream: MediaStream;
  prefix: string;
  recorder: MediaRecorder | null;
  segments: Segment[];
  nextIndex: number;
  abort: AbortController;
  pumping: boolean;
  /** The last recorder has stopped, so no more pieces will come. */
  recorded: boolean;
  stopRequested: boolean;
  /** A second recorder could not start: start the next one once this one has stopped. */
  handoff: boolean;
  /** Recorders started while another was still recording. */
  overlapped: Set<MediaRecorder>;
  /** An overlapping recorder came back empty: only one recorder at a time from now on. */
  exclusive: boolean;
  /** The server refused for good (an account or request error): stop sending this take. */
  blocked: boolean;
  ended: boolean;
  discarded: boolean;
  tracksStopped: boolean;
  error?: string;
  accountError?: string;
  rotateTimer?: ReturnType<typeof setTimeout>;
  unwatch?: () => void;
};

const IDLE: DictationSnapshot = { status: "idle", transcript: "" };
/**
 * Quiet gap that ends an utterance, measured on the page. Chrome's own end-of-speech
 * fires on any pause, which cuts a caller off mid-sentence; a natural breath is well
 * under this, a finished turn is well over it.
 */
const ENDPOINT_SILENCE_MS = 1200;
const ENDPOINT_TICK_MS = 80;
const SILENCE_RMS = 0.035;
export const TRANSCRIPTION_RESPONSE_TIMEOUT_MS = 70_000;
export const MAX_TRANSCRIPTION_RESPONSE_BYTES = 64 * 1024;
/**
 * Server dictation is cut into recordings of this length. Each is a complete file, so no
 * upload nears the server's audio cap or the response timeout, and a failure costs one
 * piece instead of the whole dictation.
 */
export const DICTATION_SEGMENT_MS = 45_000;
/** Waits before the second and third attempt at one piece. */
export const TRANSCRIPTION_RETRY_DELAYS_MS: readonly number[] = [1_000, 3_000];
/** A server's Retry-After is honoured up to this; a person is waiting for the text. */
const MAX_RETRY_WAIT_MS = 15_000;
/** Saved audio nobody came back for is dropped after this, so the device does not fill up. */
const MAX_KEPT_AGE_MS = 7 * 24 * 60 * 60 * 1000;
const TIMEOUT_ERROR = "Transcription request timed out.";
const TRANSCRIBE_ERROR = "Could not transcribe that recording.";
const ENDPOINT_UNSUPPORTED =
  "This browser can't detect when you stop talking. Use Chrome, the desktop app, or hold-to-talk in the composer.";
/** Web Speech codes that mean the cloud recognizer cannot be used. */
const WEB_SPEECH_SERVICE_ERRORS = new Set(["network", "service-not-allowed"]);

/** A permission failure should tell the person where to fix it, not just say "failed". */
export function microphoneErrorText(error: unknown): string {
  if (error instanceof Error) {
    if (error.name === "NotAllowedError" || error.name === "PermissionDeniedError") {
      return "Microphone access is blocked. Allow it in your system or browser settings, then try again.";
    }
    if (error.name === "NotFoundError") {
      return "No microphone was found. Connect a microphone and try again.";
    }
    if (error.name === "NotReadableError") {
      return "The microphone is busy in another app. Close it and try again.";
    }
  }
  return errorText(error, "Microphone failed");
}

export function webSpeechAvailable(): boolean {
  return Boolean(speechRecognitionCtor()) && !electronSpeechHost();
}

/**
 * Electron exposes webkitSpeechRecognition without a speech-service key, so
 * recognition fails with `network`. Don't treat that API as available there.
 */
function electronSpeechHost(): boolean {
  if (typeof window !== "undefined") {
    const host = window as Window & { rakazoDesktop?: unknown };
    if (host.rakazoDesktop) return true;
  }
  if (typeof navigator === "undefined") return false;
  const agent = navigator.userAgent;
  return typeof agent === "string" && agent.includes("Electron");
}

/**
 * Phones cannot hold a speech session open: `start()` replays the system tone on every
 * restart and the session ends at each pause, so Web Speech there means one beep per
 * phrase and a transcript that keeps getting cut. Server transcription records silently,
 * so prefer it on mobile browsers wherever a provider can transcribe.
 */
export function prefersServerDictation(): boolean {
  if (typeof navigator === "undefined") return false;
  const agent = typeof navigator.userAgent === "string" ? navigator.userAgent : "";
  if (/android|iphone|ipod|ipad|mobile/i.test(agent)) return true;
  // iPadOS 13+ reports a desktop Safari user agent, so fall back to touch support.
  const touchPoints = typeof navigator.maxTouchPoints === "number" ? navigator.maxTouchPoints : 0;
  return touchPoints > 1 && /macintosh/i.test(agent);
}

/** Connected server transcription should take over after Web Speech cannot run. */
export function webSpeechNeedsServerFallback(
  error: string | undefined,
  transcribe: boolean,
): boolean {
  return transcribe && typeof error === "string" && WEB_SPEECH_SERVICE_ERRORS.has(error);
}

/**
 * Web Speech ends the session on its own, and every start() plays the recognizer's tone.
 * When the browser cannot hold the microphone the sessions end the moment they begin, so
 * an immediate restart turns into a stream of beeps. Back off, then hand the microphone
 * over instead of hammering it.
 */
const RESTART_SHORT_SESSION_MS = 400;
const RESTART_BACKOFF_AFTER = 2;
const RESTART_BACKOFF_MS = 900;
const RESTART_GIVE_UP_AFTER = 4;
const RESTART_GIVE_UP_ERROR =
  "The browser keeps dropping the microphone. Start dictation again to retry.";

/** Browser recognition needs a language tag: honor the selected UI locale, including English.
 * Браузеру потрібен мовний код: виконуємо вибір інтерфейсу, зокрема англійський. */
export function recognitionLanguage(): string {
  return speechLocaleTag(getActiveUiLocale());
}

/** Omit the server language hint so multilingual providers can detect the spoken language.
 * Не передаємо мовної підказки серверу, щоб він визначав мову голосу. */
export function transcriptionLanguage(): string | undefined {
  // Server recognizers detect the audio language; a UI hint can bias another language.
  // Сервер визначає мову з аудіо; підказка від інтерфейсу може спотворити іншу мову.
  return undefined;
}

function normalizedTranscript(text: string): string {
  return text.trim().replace(/\s+/g, " ");
}

function startsWithWholePrefix(text: string, prefix: string): boolean {
  const head = prefix.toLowerCase();
  const tail = text.toLowerCase();
  if (!tail.startsWith(head)) return false;
  // Index by code point. A supplementary-plane letter is two UTF-16 units, and
  // the first half alone is not a letter.
  const point = tail.codePointAt(head.length);
  if (point === undefined) return true;
  // Punctuation ends the retained word. Letters, numbers, and "_" continue it.
  return !/[\p{L}\p{N}_]/u.test(String.fromCodePoint(point));
}

/** Join carried Web Speech with a restarted session at their longest whole-word overlap. */
export function combineTranscript(prefix: string, speechText: string): string {
  const head = normalizedTranscript(prefix);
  const tail = speechText.trim();
  if (!head) return tail;
  if (!tail) return head;
  if (startsWithWholePrefix(tail, head)) return tail;
  const normalizedTail = normalizedTranscript(tail);
  for (let start = 0; start < head.length; start += 1) {
    if (start > 0 && !/\s/u.test(head[start - 1] ?? "")) continue;
    const overlap = head.slice(start);
    if (startsWithWholePrefix(normalizedTail, overlap)) {
      return head.slice(0, start) + normalizedTail;
    }
  }
  return `${head} ${normalizedTail}`;
}

function speechRecognitionCtor(): SpeechRecognitionCtor | undefined {
  if (typeof window === "undefined") return undefined;
  const host = window as Window & {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  };
  return host.SpeechRecognition ?? host.webkitSpeechRecognition;
}

function audioContextCtor(): (new () => AudioContext) | undefined {
  if (typeof AudioContext === "function") return AudioContext;
  if (typeof window === "undefined") return undefined;
  const host = window as Window & { webkitAudioContext?: typeof AudioContext };
  return host.webkitAudioContext;
}

export class Dictation {
  private snapshot: DictationSnapshot = IDLE;
  private watchers = new Set<(s: DictationSnapshot) => void>();
  private recognition: SpeechRecognitionLike | null = null;
  private media: MediaRecorder | null = null;
  private take: Take | null = null;
  /** Takes recorded by this instance: their rows are never "restored" as if from a reload. */
  private liveTakes = new Set<string>();
  private token = 0;
  private silenceTimer: ReturnType<typeof setTimeout> | undefined;
  private restartTimer: ReturnType<typeof setTimeout> | undefined;
  private audioContext: AudioContext | null = null;
  private vadTimer: ReturnType<typeof setInterval> | undefined;
  private onFinal: ((text: string) => void) | null = null;
  private kept: Segment[] = [];
  private keptState: KeptDictation = { segments: 0, retrying: false };
  private keptWatchers = new Set<(kept: KeptDictation) => void>();
  private keptAbort: AbortController | null = null;
  private owner: string | null = null;
  private storeQueue: Promise<void> = Promise.resolve();
  private readonly store: DictationAudioStore;
  private readonly segmentMs: number;
  private readonly retryDelaysMs: readonly number[];

  constructor(
    options: {
      store?: DictationAudioStore;
      segmentMs?: number;
      retryDelaysMs?: readonly number[];
    } = {},
  ) {
    this.store = options.store ?? createDictationAudioStore();
    this.segmentMs = options.segmentMs ?? DICTATION_SEGMENT_MS;
    this.retryDelaysMs = options.retryDelaysMs ?? TRANSCRIPTION_RETRY_DELAYS_MS;
  }

  subscribe(fn: (s: DictationSnapshot) => void): () => void {
    this.watchers.add(fn);
    fn(this.snapshot);
    return () => {
      this.watchers.delete(fn);
    };
  }

  get state(): DictationSnapshot {
    return this.snapshot;
  }

  private set(next: DictationSnapshot) {
    this.snapshot = next;
    for (const watcher of [...this.watchers]) watcher(next);
  }

  /**
   * Ends whatever is running. Only "cancel" is the person throwing the recording away;
   * any other reason keeps server-path audio that has not reached the composer for a retry.
   */
  stop(reason: "submit" | "cancel" | "replace" | "keep" = "cancel") {
    this.token += 1;
    const take = this.take;
    this.take = null;
    if (take) this.endTake(take, reason === "cancel" ? "discard" : "keep");
    this.stopVad();
    clearTimeout(this.silenceTimer);
    this.silenceTimer = undefined;
    clearTimeout(this.restartTimer);
    this.restartTimer = undefined;
    const rec = this.recognition;
    this.recognition = null;
    if (rec) {
      rec.onresult = null;
      rec.onerror = null;
      rec.onend = null;
      try {
        if (reason === "submit") rec.stop();
        else rec.abort();
      } catch {
        // already stopped
      }
    }
    const media = this.media;
    this.media = null;
    if (media && media.state !== "inactive") {
      try {
        media.stop();
      } catch {
        // already stopped
      }
    }
    if (take) this.stopTracks(take);
    this.onFinal = null;
    if (this.snapshot.status !== "idle" || this.snapshot.error) this.set(IDLE);
  }

  async listen(opts: {
    mode: DictationMode;
    transcribe?: boolean;
    endpointMs?: number;
    onFinal: (text: string) => void;
  }): Promise<void> {
    this.stop("replace");
    const mine = this.token;
    const spaceId = selectedSpaceId();
    this.onFinal = opts.onFinal;
    this.set({ status: "listening", transcript: "" });
    const transcribe = Boolean(opts.transcribe);
    // An unknown capability is not a refusal: a device whose browser cannot hold the
    // microphone open goes to the server even before the voice status has loaded.
    if (prefersServerDictation() && opts.transcribe !== false) {
      await this.listenRecorder(mine, opts.mode, opts.endpointMs ?? ENDPOINT_SILENCE_MS, spaceId);
      return;
    }
    if (webSpeechAvailable()) {
      this.listenWebSpeech(
        opts.mode,
        opts.endpointMs ?? ENDPOINT_SILENCE_MS,
        mine,
        transcribe,
        spaceId,
      );
      return;
    }
    if (transcribe) {
      await this.listenRecorder(mine, opts.mode, opts.endpointMs ?? ENDPOINT_SILENCE_MS, spaceId);
      return;
    }
    this.set({
      ...IDLE,
      error:
        "This browser has no on-device dictation. Connect a voice provider with transcription, or use Chrome / the desktop app.",
    });
  }

  private listenWebSpeech(
    mode: DictationMode,
    endpointMs: number,
    mine: number,
    transcribe: boolean,
    spaceId: string | null,
  ) {
    const Ctor = speechRecognitionCtor();
    if (!Ctor) return;
    const rec = new Ctor();
    let live = true;
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = recognitionLanguage();
    // Chrome numbers results per session, so a restart starts over: keep the earlier text.
    let carried = "";
    let sessionStartedAt = Date.now();
    let shortSessions = 0;
    let sessionHeardSpeech = false;
    rec.onresult = (event) => {
      if (!live || this.token !== mine) return;
      // Each result index is a separate phrase. Matching text within this session
      // may be a real repetition; only deduplicate against a previous session.
      const session = normalizedTranscript(
        Array.from(event.results, (result) => result?.[0]?.transcript ?? "").join(" "),
      );
      if (session.trim()) {
        sessionHeardSpeech = true;
        shortSessions = 0;
      }
      const transcript = combineTranscript(carried, session);
      this.set({ status: "listening", transcript });
      if (mode !== "endpoint") return;
      // Every result means the caller was just heard, so the quiet gap is measured from
      // here. That includes Chrome finalising a phrase it already reported: its phrase
      // boundary is a breath, and the words repeat, so a window tied to new text alone
      // expires before Chrome opens its first interim for the rest of the sentence.
      if (!transcript) return;
      clearTimeout(this.silenceTimer);
      this.silenceTimer = setTimeout(() => {
        if (!live || this.token !== mine) return;
        this.finish(this.snapshot.transcript, mine);
      }, endpointMs);
    };
    rec.onerror = (event) => {
      if (!live || this.token !== mine) return;
      if (event.error === "aborted" || event.error === "no-speech") return;
      if (webSpeechNeedsServerFallback(event.error, transcribe)) {
        live = false;
        this.fallbackToServer(rec, mine, mode, endpointMs, spaceId);
        return;
      }
      live = false;
      const microphoneBlocked =
        event.error === "not-allowed" ||
        event.error === "service-not-allowed" ||
        event.error === "audio-capture";
      this.set({
        ...IDLE,
        error: microphoneBlocked
          ? "Microphone access is blocked. Allow it in your system or browser settings, then try again."
          : event.error
            ? `Dictation failed: ${event.error}`
            : "Dictation failed.",
      });
    };
    rec.onend = () => {
      if (!live || this.token !== mine) return;
      if (this.snapshot.status !== "listening") return;
      if (mode === "hold") {
        this.finish(this.snapshot.transcript, mine);
        return;
      }
      // Chrome ends the session on its own — after a pause, or its ~60s cap — and that
      // says nothing about whether the caller finished. Only the silence window above
      // ends an utterance; here we just pick the microphone back up — unless it keeps
      // ending straight away, which means the browser cannot hold the microphone at all.
      carried = this.snapshot.transcript;
      shortSessions =
        !sessionHeardSpeech && Date.now() - sessionStartedAt < RESTART_SHORT_SESSION_MS
          ? shortSessions + 1
          : 0;
      if (shortSessions >= RESTART_GIVE_UP_AFTER) {
        live = false;
        if (transcribe) {
          this.fallbackToServer(rec, mine, mode, endpointMs, spaceId);
          return;
        }
        this.releaseRecognition(rec);
        this.failListening(mine, RESTART_GIVE_UP_ERROR, true);
        return;
      }
      const restart = () => {
        if (!live || this.token !== mine) return;
        try {
          sessionStartedAt = Date.now();
          sessionHeardSpeech = false;
          rec.start();
        } catch {
          this.set({ ...IDLE, error: "Dictation ended unexpectedly." });
        }
      };
      if (shortSessions >= RESTART_BACKOFF_AFTER) {
        this.restartTimer = setTimeout(restart, RESTART_BACKOFF_MS);
        return;
      }
      restart();
    };
    this.recognition = rec;
    try {
      sessionStartedAt = Date.now();
      rec.start();
    } catch {
      if (!live || this.token !== mine) return;
      if (transcribe) {
        live = false;
        this.fallbackToServer(rec, mine, mode, endpointMs, spaceId);
        return;
      }
      live = false;
      this.releaseRecognition(rec);
      this.set({ ...IDLE, error: "Dictation failed." });
    }
  }

  private fallbackToServer(
    rec: SpeechRecognitionLike,
    mine: number,
    mode: DictationMode,
    endpointMs: number,
    spaceId: string | null,
  ) {
    const recognized = normalizedTranscript(this.snapshot.transcript);
    this.releaseRecognition(rec);
    clearTimeout(this.silenceTimer);
    this.silenceTimer = undefined;
    if (this.token !== mine) return;
    this.set({ status: "listening", transcript: recognized });
    void this.listenRecorder(mine, mode, endpointMs, spaceId);
  }

  private releaseRecognition(rec: SpeechRecognitionLike) {
    rec.onresult = null;
    rec.onerror = null;
    rec.onend = null;
    if (this.recognition === rec) this.recognition = null;
    try {
      rec.abort();
    } catch {
      // already stopped
    }
  }

  private async listenRecorder(
    mine: number,
    mode: DictationMode,
    endpointMs: number,
    spaceId: string | null,
  ) {
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
    } catch (error) {
      this.failListening(mine, microphoneErrorText(error));
      return;
    }
    if (this.token !== mine) {
      for (const track of stream.getTracks()) track.stop();
      return;
    }
    const keptSpeech = this.snapshot.transcript.trim().length > 0;
    if (mode === "endpoint" && !this.armSilence(stream, mine, endpointMs, keptSpeech)) {
      for (const track of stream.getTracks()) track.stop();
      this.failListening(mine, ENDPOINT_UNSUPPORTED);
      return;
    }
    const take: Take = {
      id: newClientId(),
      mine,
      mode,
      spaceId,
      owner: this.owner,
      persist: mode === "hold",
      stream,
      prefix: normalizedTranscript(this.snapshot.transcript),
      recorder: null,
      segments: [],
      nextIndex: 0,
      abort: new AbortController(),
      pumping: false,
      recorded: false,
      stopRequested: false,
      handoff: false,
      overlapped: new Set(),
      exclusive: false,
      blocked: false,
      ended: false,
      discarded: false,
      tracksStopped: false,
    };
    try {
      const media = this.startRecorder(take);
      take.recorder = media;
      this.media = media;
      this.take = take;
      this.liveTakes.add(take.id);
      this.watchHidden(take);
    } catch (error) {
      take.ended = true;
      clearTimeout(take.rotateTimer);
      this.stopTracks(take);
      if (this.token !== mine) return;
      this.stopVad();
      this.failListening(mine, errorText(error, "Dictation failed."));
    }
  }

  /**
   * Starts one piece of the recording on the take's stream. Every piece is its own
   * MediaRecorder, so every upload is a complete file the server can decode.
   */
  private startRecorder(take: Take): MediaRecorder {
    const media = new MediaRecorder(take.stream);
    const chunks: Blob[] = [];
    media.ondataavailable = (event) => {
      if (event.data.size) chunks.push(event.data);
    };
    media.onstop = () => {
      try {
        const type = chunks[0]?.type || media.mimeType || "audio/webm";
        this.segmentEnded(take, media, new Blob(chunks, { type }));
      } catch {
        // A broken piece must not take the rest of the dictation down with it.
      }
    };
    try {
      media.start(take.mode === "endpoint" ? 250 : undefined);
    } catch (error) {
      media.ondataavailable = null;
      media.onstop = null;
      throw error;
    }
    clearTimeout(take.rotateTimer);
    take.rotateTimer = setTimeout(() => this.rotate(take), this.segmentMs);
    return media;
  }

  /**
   * Closes the current piece and carries on in a new one. The next recorder starts before
   * the current one stops, so the pieces overlap by a few milliseconds instead of leaving
   * a gap. A browser that refuses a second recorder gets a stop-then-start handoff, which
   * can drop the moment between the two.
   */
  private rotate(take: Take) {
    const current = take.recorder;
    if (take.ended || take.stopRequested || !current || current.state === "inactive") return;
    if (take.exclusive) {
      take.handoff = true;
    } else {
      try {
        const next = this.startRecorder(take);
        take.overlapped.add(next);
        take.recorder = next;
        if (this.media === current) this.media = next;
      } catch {
        take.handoff = true;
      }
    }
    try {
      current.stop();
    } catch {
      // Recorders that cannot stop keep recording into this piece until the take ends.
      take.handoff = false;
    }
  }

  /** A home-screen app can be killed once hidden: close the piece so it is saved now. */
  private watchHidden(take: Take) {
    if (!take.persist || typeof document === "undefined") return;
    if (typeof document.addEventListener !== "function") return;
    const onChange = () => {
      if (document.visibilityState === "hidden") this.rotate(take);
    };
    document.addEventListener("visibilitychange", onChange);
    take.unwatch = () => document.removeEventListener("visibilitychange", onChange);
  }

  private stopTracks(take: Take) {
    if (take.tracksStopped) return;
    take.tracksStopped = true;
    for (const track of take.stream.getTracks()) track.stop();
  }

  private segmentEnded(take: Take, media: MediaRecorder, blob: Blob) {
    // Even silence encodes to bytes; nothing at all means this browser cannot run two.
    const overlapFailed = !blob.size && take.overlapped.has(media);
    if (overlapFailed) take.exclusive = true;
    take.overlapped.delete(media);
    let final = take.recorder === media;
    if (final && take.handoff) {
      take.handoff = false;
      if (!take.ended && !take.stopRequested) {
        try {
          const next = this.startRecorder(take);
          take.recorder = next;
          if (this.media === media) this.media = next;
          final = false;
        } catch {
          // No recorder will start again: this piece is the last one.
        }
      }
    }
    if (final) {
      take.recorder = null;
      if (this.media === media) this.media = null;
      clearTimeout(take.rotateTimer);
      take.unwatch?.();
      this.stopTracks(take);
    }
    if (blob.size && !take.discarded) this.addSegment(take, blob);
    if (take.ended) return;
    if (final) {
      take.recorded = true;
      if (this.token === take.mine) this.stopVad();
      if (take.segments.some((segment) => segment.text === undefined)) {
        this.set({ status: "transcribing", transcript: this.takeText(take) });
      }
    }
    void this.pump(take);
    // The recorder now running was started the same way: restart it on its own.
    const current = take.recorder;
    if (overlapFailed && current && take.overlapped.has(current)) this.rotate(take);
  }

  private addSegment(take: Take, blob: Blob) {
    const segment: Segment = {
      id: newClientId(),
      owner: take.owner,
      takeId: take.id,
      index: take.nextIndex,
      createdAt: Date.now(),
      spaceId: take.spaceId,
      mimeType: blob.type || "audio/webm",
      blob,
    };
    take.nextIndex += 1;
    if (!take.persist) {
      if (!take.ended) take.segments.push(segment);
      return;
    }
    this.persist(segment);
    if (!take.ended) {
      take.segments.push(segment);
      return;
    }
    // The take was set aside while this last piece was still being written.
    this.keep([segment], this.keptState.error);
  }

  /**
   * Sends the take's pieces one at a time, oldest first, so the transcript always reads in
   * the order it was spoken. A piece that still fails after its retries waits: the next
   * piece, or the end of the recording, tries it again before anything later is sent.
   */
  private async pump(take: Take): Promise<void> {
    if (take.pumping || take.ended) return;
    take.pumping = true;
    try {
      while (!take.ended && !take.blocked) {
        const segment = take.segments.find((piece) => piece.text === undefined);
        if (!segment) break;
        const outcome = await this.transcribeSegment(segment, take.abort.signal);
        if (take.ended) return;
        if (!outcome.ok) {
          take.error = outcome.error;
          take.accountError = outcome.accountError;
          if (!outcome.retryable) take.blocked = true;
          break;
        }
        take.error = undefined;
        take.accountError = undefined;
        this.saveText(segment, outcome.text);
        if (this.take === take && this.token === take.mine) {
          this.set({
            status: take.recorded ? "transcribing" : "listening",
            transcript: this.takeText(take),
          });
        }
      }
    } catch {
      // transcribeSegment reports failures as outcomes; anything else ends the pass.
    } finally {
      take.pumping = false;
    }
    if (take.recorded && !take.ended) this.settle(take);
  }

  /** The recording is over and the queue has gone as far as it can. */
  private settle(take: Take) {
    if (take.ended || this.take !== take || this.token !== take.mine) return;
    const mine = take.mine;
    const text = this.takeText(take);
    const delivered = take.segments.filter((segment) => segment.text !== undefined);
    const remaining = take.segments.filter((segment) => segment.text === undefined);
    take.ended = true;
    take.unwatch?.();
    this.take = null;
    if (!remaining.length) {
      this.finish(text, mine);
      this.forget(delivered);
      return;
    }
    if (!take.persist) {
      this.set({ status: this.snapshot.status, transcript: text });
      this.failListening(mine, take.error ?? TRANSCRIBE_ERROR);
      return;
    }
    // Whatever was recognised goes to the composer now; the rest waits for a retry.
    this.keep(remaining, take.accountError);
    this.finish(text, mine, take.error ?? TRANSCRIBE_ERROR);
    this.forget(delivered);
  }

  private endTake(take: Take, how: "keep" | "discard") {
    if (take.ended) return;
    take.ended = true;
    take.discarded = how === "discard";
    take.abort.abort();
    clearTimeout(take.rotateTimer);
    take.unwatch?.();
    if (how === "discard" || !take.persist) {
      this.forget(take.segments);
      take.segments = [];
      return;
    }
    this.keep(take.segments, take.accountError);
  }

  private takeText(take: Take): string {
    const texts = take.segments.flatMap((segment) =>
      segment.text === undefined ? [] : [segment.text],
    );
    // The recorder captures fresh audio, so matching words are intentional repetitions.
    if (take.prefix) {
      return [take.prefix, ...texts.map(normalizedTranscript)].filter(Boolean).join(" ");
    }
    return texts
      .map((text) => text.trim())
      .filter(Boolean)
      .join(" ");
  }

  private async transcribeSegment(segment: Segment, signal: AbortSignal): Promise<Outcome> {
    for (let attempt = 0; ; attempt += 1) {
      const outcome = await sendSegment(segment, signal);
      if (outcome.ok || !outcome.retryable || signal.aborted) return outcome;
      const delay = this.retryDelaysMs[attempt];
      if (delay === undefined) return outcome;
      await wait(Math.max(delay, Math.min(outcome.retryAfterMs ?? 0, MAX_RETRY_WAIT_MS)), signal);
      if (signal.aborted) return outcome;
    }
  }

  private saveText(segment: Segment, text: string) {
    segment.text = text;
    segment.blob = undefined;
    this.persist(segment);
  }

  /** Store writes run in order, so a delete always lands after the write it undoes. */
  private queueStore(work: () => Promise<void>) {
    this.storeQueue = this.storeQueue.then(work).catch(() => undefined);
  }

  private persist(segment: Segment) {
    this.queueStore(async () => {
      if (segment.forgotten) return;
      const row: StoredDictationSegment = {
        id: segment.id,
        owner: segment.owner,
        takeId: segment.takeId,
        index: segment.index,
        createdAt: segment.createdAt,
        spaceId: segment.spaceId,
        mimeType: segment.mimeType,
      };
      if (segment.text !== undefined) row.text = segment.text;
      else if (segment.blob) row.audio = await segment.blob.arrayBuffer();
      else return;
      if (segment.forgotten) return;
      await this.store.put(row);
    });
  }

  private forget(segments: Segment[]) {
    if (!segments.length) return;
    for (const segment of segments) segment.forgotten = true;
    const ids = segments.map((segment) => segment.id);
    this.queueStore(() => this.store.delete(ids));
  }

  private keep(segments: Segment[], error: string | undefined) {
    const known = new Set(this.kept.map((segment) => segment.id));
    const fresh = segments.filter(
      (segment) => !known.has(segment.id) && !segment.forgotten && segment.owner === this.owner,
    );
    if (!fresh.length) return;
    this.kept = [...this.kept, ...fresh].sort(segmentOrder);
    this.setKept({ retrying: this.keptState.retrying, error });
  }

  private setKept(next: { retrying: boolean; error?: string }) {
    this.keptState = {
      segments: this.kept.length,
      retrying: next.retrying,
      ...(next.error ? { error: next.error } : {}),
    };
    for (const watcher of [...this.keptWatchers]) watcher(this.keptState);
  }

  get keptDictation(): KeptDictation {
    return this.keptState;
  }

  subscribeKept(fn: (kept: KeptDictation) => void): () => void {
    this.keptWatchers.add(fn);
    fn(this.keptState);
    return () => {
      this.keptWatchers.delete(fn);
    };
  }

  /**
   * Scopes saved audio to the signed-in user and offers what an earlier visit left behind,
   * such as a home-screen app that was closed mid-dictation.
   */
  setOwner(owner: string | null): void {
    if (owner === this.owner) return;
    this.owner = owner;
    this.keptAbort?.abort();
    this.keptAbort = null;
    this.kept = this.kept.filter((segment) => segment.owner === owner);
    this.setKept({ retrying: false });
    if (owner !== null) void this.restoreKept(owner);
  }

  private async restoreKept(owner: string): Promise<void> {
    let rows: StoredDictationSegment[] = [];
    const listed = new Promise<void>((resolve) => {
      this.queueStore(async () => {
        try {
          rows = await this.store.list();
        } finally {
          resolve();
        }
      });
    });
    await listed;
    if (owner !== this.owner) return;
    const now = Date.now();
    const stale = rows.filter((row) => now - row.createdAt > MAX_KEPT_AGE_MS);
    if (stale.length) {
      const ids = stale.map((row) => row.id);
      this.queueStore(() => this.store.delete(ids));
    }
    const restored = rows
      .filter(
        (row) =>
          row.owner === owner &&
          now - row.createdAt <= MAX_KEPT_AGE_MS &&
          !this.liveTakes.has(row.takeId),
      )
      .map(
        (row): Segment => ({
          id: row.id,
          owner: row.owner,
          takeId: row.takeId,
          index: row.index,
          createdAt: row.createdAt,
          spaceId: row.spaceId,
          mimeType: row.mimeType,
          ...(row.text !== undefined
            ? { text: row.text }
            : row.audio
              ? { blob: new Blob([row.audio], { type: row.mimeType }) }
              : {}),
        }),
      );
    this.keep(restored, this.keptState.error);
  }

  /**
   * Sends saved pieces again, oldest first, and hands their text to `onText` in spoken
   * order. Text already recognised before a reload is reused, not paid for twice. A piece
   * that still fails stays saved with everything after it.
   */
  async retryKept(onText: (text: string) => void): Promise<void> {
    if (this.keptState.retrying || !this.kept.length) return;
    const abort = new AbortController();
    this.keptAbort = abort;
    this.setKept({ retrying: true });
    const done = new Set<string>();
    let accountError: string | undefined;
    for (;;) {
      const segment = this.kept.find((piece) => !done.has(piece.id));
      if (!segment) break;
      if (segment.text === undefined) {
        const outcome = await this.transcribeSegment(segment, abort.signal);
        if (abort.signal.aborted) return;
        if (!outcome.ok) {
          accountError = outcome.accountError;
          break;
        }
        this.saveText(segment, outcome.text);
      }
      done.add(segment.id);
    }
    if (this.keptAbort === abort) this.keptAbort = null;
    const delivered = this.kept.filter((segment) => done.has(segment.id));
    this.kept = this.kept.filter((segment) => !done.has(segment.id));
    this.setKept({ retrying: false, error: accountError });
    const text = delivered
      .map((segment) => segment.text?.trim() ?? "")
      .filter(Boolean)
      .join(" ");
    if (text) onText(text);
    this.forget(delivered);
  }

  /** The person threw the saved recording away. */
  discardKept(): void {
    this.keptAbort?.abort();
    this.keptAbort = null;
    const gone = this.kept;
    this.kept = [];
    this.forget(gone);
    this.setKept({ retrying: false });
  }

  /** Stops recording on the server path; the queue then settles the take. */
  private endRecording(): boolean {
    const take = this.take;
    if (!take || take.ended) return false;
    if (take.stopRequested) return true;
    take.stopRequested = true;
    clearTimeout(take.rotateTimer);
    const media = take.recorder;
    if (!media || media.state === "inactive") return true;
    try {
      media.stop();
    } catch {
      this.segmentEnded(take, media, new Blob([]));
    }
    return true;
  }

  private armSilence(
    stream: MediaStream,
    mine: number,
    endpointMs: number,
    alreadyHeard = false,
  ): boolean {
    const Ctor = audioContextCtor();
    if (!Ctor) return false;
    try {
      const ctx = new Ctor();
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);
      this.audioContext = ctx;
      if (ctx.state === "suspended") void ctx.resume();
      const data = new Uint8Array(analyser.fftSize);
      let heardSpeech = alreadyHeard;
      let silentFor = 0;
      this.vadTimer = setInterval(() => {
        const media = this.media;
        if (this.token !== mine || !media || media.state === "inactive") return;
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (const sample of data) {
          const n = (sample - 128) / 128;
          sum += n * n;
        }
        const rms = Math.sqrt(sum / data.length);
        if (rms > SILENCE_RMS) {
          heardSpeech = true;
          silentFor = 0;
          return;
        }
        if (!heardSpeech) return;
        silentFor += ENDPOINT_TICK_MS;
        if (silentFor < endpointMs) return;
        this.stopVad();
        this.endRecording();
      }, ENDPOINT_TICK_MS);
      return true;
    } catch {
      this.stopVad();
      return false;
    }
  }

  private stopVad() {
    clearInterval(this.vadTimer);
    this.vadTimer = undefined;
    const ctx = this.audioContext;
    this.audioContext = null;
    if (ctx) void ctx.close().catch(() => undefined);
  }

  submitHold() {
    if (this.endRecording()) return;
    this.finish(this.snapshot.transcript, this.token);
  }

  private failListening(mine: number, message: string, showError = false) {
    if (this.token !== mine) return;
    const prefix = normalizedTranscript(this.snapshot.transcript);
    if (prefix) {
      this.finish(prefix, mine, showError ? message : undefined);
      return;
    }
    this.set({ ...IDLE, error: message });
  }

  private finish(text: string, mine: number, error?: string) {
    if (this.token !== mine) return;
    const trimmed = text.trim();
    const onFinal = this.onFinal;
    this.stop("submit");
    if (error) this.set({ ...IDLE, error });
    if (trimmed) onFinal?.(trimmed);
  }
}

function segmentOrder(a: Segment, b: Segment): number {
  if (a.takeId === b.takeId) return a.index - b.index;
  return a.createdAt - b.createdAt || a.index - b.index;
}

/**
 * One attempt at one piece. Network failures, timeouts, 5xx, 408 and 429 may succeed if
 * asked again; any other answer (a rejected key, no credit, a refused request) will not.
 */
async function sendSegment(segment: Segment, outer: AbortSignal): Promise<Outcome> {
  const blob = segment.blob;
  if (!blob) return { ok: true, text: segment.text ?? "" };
  const abort = new AbortController();
  const onOuter = () => abort.abort(outer.reason);
  if (outer.aborted) onOuter();
  else outer.addEventListener("abort", onOuter, { once: true });
  const timer = setTimeout(
    () => abort.abort(new Error(TIMEOUT_ERROR)),
    TRANSCRIPTION_RESPONSE_TIMEOUT_MS,
  );
  try {
    const audioBase64 = await blobToBase64(blob);
    const res = await withAbort(
      fetch("/api/voice/transcribe", {
        method: "POST",
        headers: withSpaceHeaders({ "content-type": "application/json" }, segment.spaceId),
        credentials: "include",
        body: JSON.stringify({
          audioBase64,
          mimeType: blob.type,
          language: transcriptionLanguage(),
        }),
        signal: abort.signal,
      }),
      abort.signal,
    );
    let body: { text?: string; error?: string; code?: string };
    try {
      body = await readTranscriptionBody(res, abort.signal);
    } catch (error) {
      if (abort.signal.aborted) throw error;
      // An answer that cannot be read will not read better the second time.
      return { ok: false, error: errorText(error, TRANSCRIBE_ERROR), retryable: false };
    }
    if (!res.ok) {
      const accountError = voiceAccountErrorText(body);
      return {
        ok: false,
        error: accountError ?? body.error ?? TRANSCRIBE_ERROR,
        accountError,
        retryable: res.status >= 500 || res.status === 408 || res.status === 429,
        retryAfterMs: parseRetryAfter(res.headers.get("retry-after")),
      };
    }
    return { ok: true, text: body.text ?? "" };
  } catch (error) {
    if (outer.aborted) return { ok: false, error: TRANSCRIBE_ERROR, retryable: false };
    // Browsers may reject an aborted fetch as AbortError instead of the abort reason; only
    // the deadline aborts while the take is still live, so either way it timed out.
    const timedOut =
      (abort.signal.reason instanceof Error && abort.signal.reason.message === TIMEOUT_ERROR) ||
      (error instanceof Error && (error.message === TIMEOUT_ERROR || error.name === "AbortError"));
    if (timedOut) return { ok: false, error: TIMEOUT_ERROR, retryable: true };
    return { ok: false, error: errorText(error, TRANSCRIBE_ERROR), retryable: true };
  } finally {
    clearTimeout(timer);
    outer.removeEventListener("abort", onOuter);
  }
}

function wait(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }
    const done = () => {
      clearTimeout(timer);
      signal.removeEventListener("abort", done);
      resolve();
    };
    const timer = setTimeout(done, ms);
    signal.addEventListener("abort", done, { once: true });
  });
}

async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

async function readTranscriptionBody(
  response: Response,
  signal: AbortSignal,
): Promise<{ text?: string; error?: string; code?: string }> {
  const declared = Number(response.headers.get("content-length") ?? 0);
  if (Number.isFinite(declared) && declared > MAX_TRANSCRIPTION_RESPONSE_BYTES) {
    cancelResponse(response);
    throw new Error("Transcription response is too large.");
  }
  const bytes = await readBoundedResponseBytes(response, {
    maxBytes: MAX_TRANSCRIPTION_RESPONSE_BYTES,
    tooLargeMessage: "Transcription response is too large.",
    read: (operation) => withAbort(operation(), signal),
  });
  try {
    const parsed = JSON.parse(new TextDecoder().decode(bytes)) as unknown;
    return parsed && typeof parsed === "object"
      ? (parsed as { text?: string; error?: string; code?: string })
      : {};
  } catch {
    return {};
  }
}

function withAbort<T>(promise: Promise<T>, signal: AbortSignal): Promise<T> {
  if (signal.aborted) return Promise.reject(signal.reason ?? new Error("Request aborted."));
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(signal.reason ?? new Error("Request aborted."));
    signal.addEventListener("abort", onAbort, { once: true });
    promise.then(
      (value) => {
        signal.removeEventListener("abort", onAbort);
        resolve(value);
      },
      (error: unknown) => {
        signal.removeEventListener("abort", onAbort);
        // Prefer abort reason over a bare AbortError from fetch/read.
        reject(signal.aborted ? (signal.reason ?? error) : error);
      },
    );
  });
}

function cancelResponse(response: Response): void {
  try {
    void Promise.resolve(response.body?.cancel()).catch(() => undefined);
  } catch {
    // Response cleanup is best-effort.
  }
}

export const dictation = new Dictation();
